import { useEscapeToBack } from "@/components/keyboard/useEscapeToBack.ts";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

/**
 * The height a control in a panel header takes, on its own.
 *
 * Separate from {@link panelHeaderControlClass} because not every control in
 * this row is an icon in a square. One that carries a label — a share button
 * reading "Share" — has to sit level with its icon-only neighbours without
 * being squeezed into their width. One definition either way, so a change to
 * the row's height reaches both kinds.
 */
export const panelHeaderControlHeightClass = "h-10 md:h-7";

/**
 * Shared look for a panel header's icon controls, so every naked icon on a
 * header bar reads as one group: no chrome at rest, and the glyph at full
 * strength — the same ink as the title beside it.
 *
 * Owns the glyph size as well as the box, so the chrome scales from here rather
 * than from a `size-` class repeated at every call site. 16px in a 28px box
 * leaves 6px of empty box around the glyph, which is exactly what
 * `panelTrailingIconClass` cancels — the two numbers move together.
 *
 * Bigger on a phone, where a thumb wants the 40px the pivot pages' actions
 * already use. Viewport-keyed rather than container-keyed: this is about the
 * input device, which a panel's width does not tell you.
 *
 * `shrink-0` belongs to the box rather than to each caller: a round control
 * pressed into an oval is never what a header row wants when its title runs
 * long, and the title beside it truncates precisely so it doesn't have to be.
 */
export const panelHeaderControlClass = cn(
  panelHeaderControlHeightClass,
  "w-10 shrink-0 [&_svg]:size-5 md:w-7 md:[&_svg]:size-4",
);

/**
 * The same controls, one size on every screen, for the page-actions row under
 * the header rather than the header itself.
 *
 * Deliberately without the phone step up to 40px above. A header carries two or
 * three controls and can spend the extra width on a thumb; that row carries a
 * whole filter axis spelled out, and the larger box there costs options that
 * then have to be scrolled to — worst on the narrow screen the bigger target
 * was meant to help. 28px still clears the 24px WCAG 2.5.8 target minimum
 * `toggle.tsx`'s small switch is sized against.
 *
 * Lives here beside its sibling so the two scales are read and changed
 * together.
 */
export const panelToolbarControlClass = "size-7 [&_svg]:size-4";

/**
 * Pulls a trailing icon button out to the panel edge by its own optical padding.
 *
 * `panelHeaderControlClass` leaves 6px of empty box around its glyph. Sit that
 * box on the gutter and the *glyph* lands 6px inside it, while every row of
 * content below sits flush — a control that looks inset even though its box is
 * aligned. This cancels the near side so the mark lines up with the ink under it.
 *
 * Only for an icon button that ends its row: a chip or a labelled trigger has no
 * such padding to cancel, and pulling one pushes it past the gutter instead.
 */
export const panelTrailingIconClass = "-mr-1.5";

/**
 * The one page-title treatment every panel header uses, so the title reads the
 * same whichever column it is in and whichever view the canvas is showing.
 *
 * A class rather than a component because the callers can't share one element:
 * the month title lives inside a popover trigger, where a heading would be
 * invalid content, and the thread title layers rename affordances over
 * `SiteHeaderTitle`. They can share the look without sharing the tag.
 *
 * One size at every desktop width: a page's name is the same thing in a narrow
 * column as in a wide one. The `@5xl` pair is load-bearing rather than a
 * redundant restatement — tailwind-merge keys on the modifier, so an unmodified
 * `text-base` cannot displace `SiteHeaderTitle`'s `@5xl:text-xl`.
 *
 * On a phone it takes the serif display face the thread list wears: mobile shows
 * one panel at a time, so its header is the page's own title rather than one
 * column's label among several. `text-2xl` lands at the 20px that title sets,
 * since this app's scale sits a step down.
 */
export const panelHeaderTitleClass =
  "min-w-0 truncate text-left font-serif text-2xl font-normal tracking-tight md:font-sans md:text-base md:font-medium md:tracking-normal @5xl:text-base @5xl:tracking-normal";

/**
 * The title of a screen reached by drilling into another one — a routine opened
 * from the list, a thread opened from it, a settings panel behind its own back
 * control.
 *
 * The same treatment a canvas page title gets on a wide screen, and deliberately
 * not the serif `panelHeaderTitleClass` switches to on a phone: the control in
 * front of it already says this is a second level, and the display face would
 * make the child look like the more important of the two.
 *
 * The `@5xl` pair is load-bearing for the reason given above.
 */
export const panelHeaderCrumbTitleClass =
  "min-w-0 truncate text-left text-base font-medium tracking-normal @5xl:text-base @5xl:tracking-normal";

/**
 * The trail a drilled-in child screen shows before its own title: the parent's
 * name as the way back, then a forward chevron reading as "you are inside it".
 *
 * A breadcrumb rather than a second back chevron, because a child screen usually
 * sits under chrome that already has one, and stacking two invites the question
 * of which goes where.
 *
 * Shared by the settings panels and the canvas pivots so drilling into contacts
 * is the same gesture wherever the list is being shown.
 */
export const PanelBreadcrumb = ({
  parentLabel,
  onBack,
  statsigName = "panel-breadcrumb-back",
}: {
  parentLabel: string;
  onBack: () => void;
  statsigName?: string;
}) => (
  <>
    {/* border-0 strips the Button base's 1px transparent border, so the parent
        title starts at exactly the x the list title occupies — drilling in and
        backing out must not shift the text. */}
    <Button
      variant="link"
      size="fit"
      onClick={onBack}
      className="shrink-0 border-0 p-0 text-base font-semibold text-muted-foreground no-underline hover:text-foreground hover:no-underline"
      data-statsig-name={statsigName}
    >
      {parentLabel}
    </Button>
    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
  </>
);

/**
 * The one header shell every split-panel column uses — home threads/calendar and
 * thread conversation/canvas all route through this so the headers can't drift.
 *
 * Height and side padding are **viewport-based** (`md:` / `lg:`), not
 * container-based, on purpose: each panel is its own `@container`, so a
 * container-query height (as `SiteHeaderWrapper` uses) resolves
 * against the panel's own width and a narrow panel ends up shorter than a wide
 * one. Viewport breakpoints keep every panel header the same height on a given
 * screen. It still establishes its own `@container` so content like
 * `CalendarHeader` keeps its compact/full behavior.
 */
export const SplitPanelHeader = ({
  children,
  className,
  grows = false,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Let the row take more than one header's height when its own content needs
   * it — an inline editor that soft-wraps, say. The height becomes a floor
   * instead of a fixed value.
   *
   * Here rather than at the caller so the numbers stay in one file: a caller
   * relaxing them has to restate them, and then a change to the shell's height
   * silently stops reaching whoever copied it. Off by default, and it should
   * stay off unless a header genuinely grows — the fixed height is what keeps
   * every panel's row level with the others on screen.
   */
  grows?: boolean;
}) => (
  <header className="@container contain-layout shrink-0">
    {/* Side inset comes from `SplitPanelFrame`'s `--panel-gutter`, so the header
        and the body beneath it can't drift apart. The fallback keeps a header
        rendered outside a frame from collapsing to zero padding; inside one,
        the frame always wins. Height stays viewport-based for the reason in the
        component doc above.

        The background sits here rather than on the `<header>` so a caller's own
        `bg-*` displaces it through tailwind-merge. Painted on the parent it
        would only ever be covered, and a header that later grew any padding
        would leak the page colour around a card that is not that colour. */}
    <div
      className={cn(
        // 64 on a phone, 56 on desktop: the desktop row seats a 28px control
        // with 12px of slack a side, while a phone carries a 40px control and a
        // 20px display title in the same row and needs the extra height.
        "flex items-center gap-1 bg-background px-[var(--panel-gutter,1rem)] @3xl:gap-1.5",
        grows ? "min-h-16 md:min-h-14" : "h-16 md:h-14",
        className,
      )}
    >
      {children}
    </div>
  </header>
);

/**
 * Back button + screen-level Escape-to-back for a panel header — the same
 * behavior `SiteHeaderContent` gives (both share {@link useEscapeToBack}), as a
 * standalone control so a split-panel header can host it. `onBack` may veto
 * navigation by returning `false`.
 */
export const SplitPanelBackButton = ({
  onBack,
  tooltip = "Back",
  className,
}: {
  onBack?: () => boolean;
  tooltip?: string;
  /**
   * Merged after the default sizing, so a header that dresses its controls with
   * {@link panelHeaderControlClass} can bring this one along. Resizing the
   * chevron needs `[&_svg]:size-…` — a class on the button cannot reach a child.
   */
  className?: string;
}) => {
  const handleBack = useEscapeToBack(onBack);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          // `panelTrailingIconClass` mirrored onto the leading edge: the same
          // 6px of empty box around a 16px glyph, cancelled on the near side.
          // `mr-2` rather than `mr-1`: the box already eats its own padding on
          // the near side, so the gap the eye reads between the glyph and the
          // title is the margin alone.
          className={cn("-ml-1.5 mr-2", className)}
          data-statsig-name="nav-back"
        >
          <ChevronLeft className="size-6" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};
